import { Permission, type UserRole } from '@today/types'
import { roleAtLeast } from '@/auth/role-order'
import { canOpenAdmin } from '@/modules/admin-sections/visible-sections'
import type { NavigationGroup, NavigationItem } from './config.js'

export interface NavigationViewer {
	role: UserRole
	/** Permission atoms from `/auth/me`, for permission-based predicates. */
	permissions?: readonly string[]
}

/**
 * Closed-set predicate evaluators for `visibleIf` strings.
 *
 * Template ships with no predicates registered. To add one:
 *   1. Extend the `visibleIf` union in `config.ts` (replace `never` with
 *      `'your-predicate-name' | ...`).
 *   2. Add the predicate's evaluator function to PREDICATES below.
 *
 * Closed-set discipline (no arbitrary functions) keeps nav gating
 * inspectable at a glance.
 */
const PREDICATES: Record<string, (viewer: NavigationViewer) => boolean> = {
	'can-read-customers': (viewer) => viewer.permissions?.includes(Permission.CUSTOMER_READ) ?? false,
	'can-read-products': (viewer) => viewer.permissions?.includes(Permission.PRODUCT_READ) ?? false,
	'can-read-offers': (viewer) => viewer.permissions?.includes(Permission.OFFER_READ) ?? false,
	'can-manage-locales': (viewer) => viewer.permissions?.includes(Permission.TENANT_MANAGE_SETTINGS) ?? false,
	'can-read-content': (viewer) => viewer.permissions?.includes(Permission.CONTENT_READ) ?? false,
	'can-approve-content': (viewer) => viewer.permissions?.includes(Permission.CONTENT_APPROVE) ?? false,
	'can-manage-content-types': (viewer) => viewer.permissions?.includes(Permission.CONTENT_TYPES_MANAGE) ?? false,
	'can-open-admin': (viewer) => canOpenAdmin(viewer),
}

class UnknownNavigationPredicateError extends Error {
	constructor(predicate: string) {
		super(`Unknown navigation predicate: "${predicate}". Add it to PREDICATES in filterNav.ts.`)
		this.name = 'UnknownNavigationPredicateError'
	}
}

/**
 * Whether the closed-set predicate `predicate` holds for `viewer`. The single
 * source of the atom-to-link rules: the legacy sidebar and the per-plane shells
 * (TO-636 workspace nav) both evaluate through it, so a rule cannot drift
 * between them.
 *
 * @throws UnknownNavigationPredicateError when no evaluator is registered.
 */
export function navPredicateHolds(predicate: string, viewer: NavigationViewer): boolean {
	const evaluator = PREDICATES[predicate]
	if (!evaluator) throw new UnknownNavigationPredicateError(predicate)
	return evaluator(viewer)
}

function isItemVisible(item: NavigationItem, viewer: NavigationViewer): boolean {
	if (item.visibleFor && !item.visibleFor.includes(viewer.role)) return false
	if (item.minRole && !roleAtLeast(viewer.role, item.minRole)) return false

	if (item.visibleIf && !navPredicateHolds(item.visibleIf, viewer)) return false

	return true
}

function isGroupVisible(group: NavigationGroup, viewer: NavigationViewer): boolean {
	if (group.visibleFor && !group.visibleFor.includes(viewer.role)) return false
	return true
}

export function filterNav(config: NavigationGroup[], viewer: NavigationViewer): NavigationGroup[] {
	return config
		.filter((group) => isGroupVisible(group, viewer))
		.map((group) => ({
			...group,
			items: group.items.filter((item) => isItemVisible(item, viewer)),
		}))
}
